import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { ChevronLeft, AlertCircle, Phone, Globe, MessageSquare, Search } from "lucide-react";

const COUNTRIES = [
  { code: "DE", name: "Deutschland" }, { code: "AT", name: "Österreich" }, { code: "CH", name: "Schweiz" },
  { code: "GB", name: "United Kingdom" }, { code: "IE", name: "Ireland" }, { code: "FR", name: "France" },
  { code: "BE", name: "Belgique" }, { code: "NL", name: "Nederland" }, { code: "ES", name: "España" },
  { code: "PT", name: "Portugal" }, { code: "IT", name: "Italia" }, { code: "GR", name: "Ελλάδα" },
  { code: "DK", name: "Danmark" }, { code: "SE", name: "Sverige" }, { code: "NO", name: "Norge" },
  { code: "FI", name: "Suomi" }, { code: "PL", name: "Polska" }, { code: "CZ", name: "Česko" },
  { code: "US", name: "United States" }, { code: "CA", name: "Canada" }, { code: "MX", name: "México" },
  { code: "BR", name: "Brasil" }, { code: "AU", name: "Australia" }, { code: "NZ", name: "New Zealand" },
  { code: "JP", name: "日本" }, { code: "IN", name: "India" }, { code: "ZA", name: "South Africa" },
];

function guessCountry() {
  try {
    const loc = navigator.language || "";
    const m = loc.match(/-([A-Z]{2})/);
    if (m && COUNTRIES.some((c) => c.code === m[1])) return m[1];
    const map = { de: "DE", en: "GB", es: "ES", fr: "FR", it: "IT", pt: "PT", nl: "NL" };
    return map[loc.slice(0, 2).toLowerCase()] || "DE";
  } catch { return "DE"; }
}

export default function Safety() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("safety"));
  const [country, setCountry] = useState(guessCountry());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async (cc) => {
    setLoading(true);
    try { const { data } = await api.get(`/safety/helplines${cc ? `?country=${cc}` : ""}`); setData(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(country); }, [country, load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return COUNTRIES;
    const s = q.trim().toLowerCase();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s));
  }, [q]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="safety-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="safety-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <span className="inline-flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{t("safety")}</span>
          <h1 className="font-display text-2xl text-foreground">{t("you_re_not_alone")}</h1>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-4">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{t("if_you_are_in_immediate_danger_call")}</p>
          {data?.emergency_number ? (
            <a href={`tel:${data.emergency_number}`} data-testid="safety-emergency" className="mt-2 inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-1.5 text-sm font-semibold text-white">
              <Phone className="h-4 w-4" /> {data.emergency_number}
            </a>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("your_country")}</p>
        <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_country")} className="h-8 border-0 bg-transparent px-0 focus-visible:ring-0" data-testid="safety-country-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          {filtered.slice(0, 12).map((c) => (
            <button key={c.code} onClick={() => setCountry(c.code)} data-testid={`country-${c.code}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${country === c.code ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{c.name}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner label={t("common.loading")} /> : (
        <div className="space-y-2">
          {(data?.helplines || []).map((h, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`helpline-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">{h.name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {h.hours ? <span>{h.hours}</span> : null}
                  {h.lang ? <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold text-primary">{String(h.lang).toUpperCase()}</span> : null}
                </div>
              </div>
              <a href={h.phone ? `tel:${h.phone}` : h.url} target={h.phone ? undefined : "_blank"} rel="noreferrer" data-testid={`helpline-action-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">
                {h.type === "sms" ? <MessageSquare className="h-3.5 w-3.5" /> : h.type === "chat" || h.type === "web" ? <Globe className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
                {h.phone || t("open")}
              </a>
            </div>
          ))}
        </div>
      )}

      {data?.disclaimer ? <p className="pt-2 text-xs italic leading-relaxed text-muted-foreground">{data.disclaimer}</p> : null}
      <p className="pb-4 text-[11px] leading-relaxed text-muted-foreground">{t("my_within_is_not_a_medical_device_or_a")}</p>
    </div>
  );
}
